// "Inside the app" — the one fact a website page needs to know.
//
// Owner, 2026-10-09: "I don't want anything linking out to an external site
// I want all pages in the app." The website's tools are the app's tools, so
// the app renders the website's own page components — but without the
// website's masthead, menu, index strip and footer, which inside a phone app
// are a second navigation stacked on top of the tab bar.
//
// Two pieces:
//   · a context that is true under /m, which TickerShell reads to drop its
//     chrome;
//   · `appPath`, which turns a website address into its in-app address
//     (/leaderboard → /m/t/leaderboard, /account → /m/account), used by every
//     link the app draws itself and by the click interceptor in RbAppShell
//     for links the website's pages draw.

import { createContext, useContext } from "react";

export const InAppContext = createContext(false);

/** True when rendering inside the RaisedBeef app's frame. */
export function useInApp(): boolean {
  return useContext(InAppContext);
}

/** /ticker/kansas → KS. The website's state tickers are named by slug. */
const STATE_ALPHA: Record<string, string> = {
  alabama: "AL", arizona: "AZ", arkansas: "AR", california: "CA", colorado: "CO", florida: "FL", georgia: "GA", idaho: "ID", illinois: "IL",
  indiana: "IN", iowa: "IA", kansas: "KS", kentucky: "KY", louisiana: "LA", michigan: "MI", minnesota: "MN", mississippi: "MS", missouri: "MO",
  montana: "MT", nebraska: "NE", nevada: "NV", "new-mexico": "NM", "new-york": "NY", "north-carolina": "NC", "north-dakota": "ND", ohio: "OH",
  oklahoma: "OK", oregon: "OR", pennsylvania: "PA", "south-carolina": "SC", "south-dakota": "SD", tennessee: "TN", texas: "TX", utah: "UT",
  virginia: "VA", washington: "WA", "west-virginia": "WV", wisconsin: "WI", wyoming: "WY", manitoba: "MB", saskatchewan: "SK",
};

/** Website addresses that the app keeps on its own screens. */
const OWN: Record<string, string> = {
  "/": "/m",
  "/account": "/m/account",
  "/pricing": "/m/account",
  // The website's purchase page is the app's own plan screen (Apple's purchase).
  "/subscribe": "/m/subscribe",
  "/barns": "/m/barns",
  // The Indexes tool (appTools INDEXES) opens the app's own screen, not the
  // website's /index page in the frame.
  "/index": "/m/indexes",
  "/index/methodology": "/m/index/methodology",
  "/index/methodology/daily": "/m/index/methodology/daily",
  // Tools that have their own screen in the app (designed one at a time with
  // the owner, 2026-10-09). A tool not listed here renders the website's page
  // inside the frame at /m/t/<path>.
  "/weekly-averages": "/m/wavg",
  "/quotes-board": "/m/qb",
  "/leaderboard": "/m/leaderboard",
  "/toppers": "/m/toppers",
  "/boxed-beef": "/m/boxed-beef",
  "/rb/boxed-beef": "/m/boxed-beef",
  "/fed-cattle": "/m/fed-cattle",
  "/steer-tracker": "/m/steer-tracker",
  "/fat-cattle": "/m/fat-cattle",
  "/reports": "/m/reports",
  "/sale-reports": "/m/reports",
  "/rb/fed-cattle": "/m/fed-cattle",
  "/packer-margin": "/m/packer-margin",
  "/cattle-on-feed": "/m/cattle-on-feed",
  "/border": "/m/border",
  "/rb/cattle-on-feed": "/m/cattle-on-feed",
  "/diesel": "/m/diesel",
  "/receipts": "/m/receipts",
  "/clara": "/m/clara",
  "/clara-pro": "/m/clara-pro",
  // CMC Live lives inside this app (owner, 2026-10-25: "it replaces indexes in
  // the middle"). The public web page stays for Facebook links.
  "/cmc-live": "/m/live",
};

/**
 * The in-app address for a website path. Already-in-app paths come back as
 * they are; anything else goes under /m/t/, where the website's page renders
 * inside the frame.
 */
export function appPath(path: string): string {
  if (!path.startsWith("/")) return path;
  const [p, rest = ""] = splitQuery(path);
  if (p === "/m" || p.startsWith("/m/")) return path;
  if (OWN[p]) return OWN[p] + rest;
  const stTicker = /^\/ticker\/([a-z-]+)\/?$/.exec(p);
  if (stTicker) {
    const alpha = STATE_ALPHA[stTicker[1]];
    if (alpha) return `/m/states/${alpha.toLowerCase()}?view=board`;
  }
  if (p === "/states") return "/m/barns";
  const day = /^\/index\/([a-z0-9_]+)\/daily\/(\d{4}-\d{2}-\d{2})\/?$/.exec(p);
  if (day) return `/m/index/${day[1]}/day/${day[2]}`;
  const map = /^\/(\d+)-wt-map\/?$/.exec(p);
  if (map) return `/m/maps?band=${Number(map[1]) * 100}`;
  const liveBarn = /^\/cmc-live\/b\/([^/]+)\/?$/.exec(p);
  if (liveBarn) return `/m/live/b/${liveBarn[1]}${rest}`;
  const fat = /^\/fat-cattle\/([^/]+)\/?$/.exec(p);
  if (fat) return `/m/fat-cattle/${fat[1]}${rest}`;
  const barn = /^\/barn\/([^/]+)\/(ticker|leaderboard|live)\/?$/.exec(p);
  if (barn) {
    const view = barn[2] === "leaderboard" ? "leaders" : barn[2] === "live" ? "report" : "board";
    const q = new URLSearchParams(rest.startsWith("?") ? rest.slice(1).split("#")[0] : "");
    q.set("view", view);
    return `/m/barns/${barn[1]}?${q.toString()}`;
  }
  return `/m/t${p}${rest}`;
}

function splitQuery(path: string): [string, string] {
  const i = path.search(/[?#]/);
  return i === -1 ? [path, ""] : [path.slice(0, i), path.slice(i)];
}

/** The website address an in-app address stands for — the inverse of appPath
 *  for the screens the app draws itself. Used to ask the website's gate
 *  whether a tool needs an account. */
export function sitePath(path: string): string {
  const back = Object.entries(OWN).find(([, a]) => a === path);
  if (back) return back[0];
  const m = /^\/m\/maps\?band=(\d+)$/.exec(path);
  if (m) return `/${Number(m[1]) / 100}-wt-map`;
  return path.replace(/^\/m\/t(?=\/)/, "");
}

/**
 * The website page whose plan an app screen follows — so a person has the
 * same access in the app as on raisedbeef.com (owner, 2026-10-13: "when they
 * login on the app they have the same access they do on the website").
 *
 * ⚠️ EXPLICIT, NOT sitePath(). sitePath inverts the link map, and that map
 * has aliases ("/rb/cattle-on-feed" before "/cattle-on-feed"), so the inverse
 * can land on a path the website's gate does not list and read as free. Every
 * screen the app draws itself is named here; the website's own pages inside
 * the frame (/m/t/…) are their own path. Anything else — Home, Live Ticker,
 * Tools, Alerts, You, the account — maps to itself and is free, as the
 * homepage is.
 */
export function gatePath(pathname: string, search = ""): string {
  const p = pathname.replace(/\/+$/, "") || "/m";
  const rules: [RegExp, (m: RegExpExecArray) => string][] = [
    [/^\/m\/t(\/.*)$/, (m) => m[1]],
    [/^\/m\/indexes$/, () => "/index"],
    [/^\/m\/live$/, () => "/cmc-live"],
    [/^\/m\/live\/b\/([^/]+)$/, (m) => `/cmc-live/b/${m[1]}`],
    [/^\/m\/index\/methodology(\/daily)?$/, () => "/index/methodology"],
    [/^\/m\/index\/([^/]+)\/day\/([^/]+)$/, (m) => `/index/${m[1]}/daily/${m[2]}`],
    [/^\/m\/index\/([^/]+)$/, (m) => `/index/${m[1]}`],
    [/^\/m\/wavg$/, () => "/weekly-averages"],
    [/^\/m\/qb$/, () => "/quotes-board"],
    [/^\/m\/leaderboard$/, () => "/leaderboard"],
    [/^\/m\/toppers$/, () => "/toppers"],
    [/^\/m\/maps$/, () => {
      const band = Number(new URLSearchParams(search).get("band"));
      return `/${band >= 400 && band <= 1000 ? band / 100 : 4}-wt-map`;
    }],
    [/^\/m\/boxed-beef$/, () => "/boxed-beef"],
    [/^\/m\/fed-cattle$/, () => "/fed-cattle"],
    [/^\/m\/packer-margin$/, () => "/packer-margin"],
    [/^\/m\/cattle-on-feed$/, () => "/cattle-on-feed"],
    [/^\/m\/border$/, () => "/border"],
    [/^\/m\/diesel$/, () => "/diesel"],
    [/^\/m\/receipts$/, () => "/receipts"],
    [/^\/m\/steer-tracker$/, () => "/steer-tracker"],
    [/^\/m\/fat-cattle$/, () => "/fat-cattle"],
    [/^\/m\/fat-cattle\/([^/]+)$/, (m) => `/fat-cattle/${m[1]}`],
    [/^\/m\/reports$/, () => "/reports"],
    [/^\/m\/reports\/([^/]+)$/, (m) => `/barn/${m[1]}/live`],
    [/^\/m\/barns$/, () => "/barns"],
    [/^\/m\/barns\/([^/]+)$/, (m) => `/barn/${m[1]}`],
    [/^\/m\/states\/([^/]+)$/, (m) => `/ticker/${m[1]}`],
    // Clara Light's barn landing page is a barn's page; its topics are Clara.
    [/^\/m\/clara\/barn\/([^/]+)$/, (m) => `/barn/${m[1]}`],
    [/^\/m\/clara(\/[^/]+)?$/, () => "/clara"],
  ];
  for (const [re, to] of rules) {
    const m = re.exec(p);
    if (m) return to(m);
  }
  return p;
}

